/**
 * product-carousel.js
 *
 * Controls the product image carousel and zoom lightbox in product-hero.
 * One ProductCarousel instance is created per [data-carousel] element.
 *
 * Depends on:
 *   global.js — announceToScreenReader(), focusTrap(), releaseFocusTrap()
 *
 * Custom events dispatched:
 *   hattie:lightbox-opened        (consumed by site-header.js)
 *   hattie:lightbox-closed        (consumed by site-header.js)
 *   hattie:carousel-slide-change  (consumed by product-model-slide.js)
 */

class ProductCarousel {
  /**
   * @param {HTMLElement} el — the [data-carousel] element
   */
  constructor(el) {
    this.el = el;
    this.sectionId = el.dataset.sectionCarousel;

    this.track = el.querySelector('[data-carousel-track]');
    this.slides = Array.from(el.querySelectorAll('[data-slide-index]'));
    this.dots = Array.from(el.querySelectorAll('[data-carousel-dot]'));
    this.prevBtn = el.querySelector('[data-carousel-prev]');
    this.nextBtn = el.querySelector('[data-carousel-next]');
    this.zoomBtn = el.querySelector('[data-zoom-open]');
    this.dragHint = el.querySelector('[data-drag-hint]');

    this.total = this.slides.length;
    this.currentIndex = 0;

    const section = el.closest('.product-hero');
    this.lightbox = section ? section.querySelector('[data-lightbox]') : null;
    this.lightboxClose = this.lightbox
      ? this.lightbox.querySelector('[data-lightbox-close]')
      : null;
    this.lightboxImg = this.lightbox
      ? this.lightbox.querySelector('.product-hero__lightbox-image')
      : null;

    this._lightboxTrigger = null;
    this._lightboxEscHandler = null;
    this._touchTracking = false;
    this._touchStartX = 0;
    this._touchStartY = 0;

    this.images = this._parseImageData();

    if (this.total > 0) {
      this._initSlides();
      this._bindNavigation();
      this._bindTouchSwipe();
      this._bindLightbox();
      this._bindVariantImageChange();
    }
  }

  _initSlides() {
    this._setTrackOffset(0);
    this.slides.forEach((slide, i) => {
      const isActive = i === 0;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      // Inactive slides can contain the focusable 3D model viewport; inert
      // keeps aria-hidden off-screen content out of the tab order.
      slide.toggleAttribute('inert', !isActive);
    });
    this._syncDots(0);
    this._syncSlideControls(0);
  }

  _bindNavigation() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.prev();
      });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.next();
      });
    }

    this.dots.forEach((dot) => {
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        const index = Number.parseInt(dot.getAttribute('data-carousel-dot'), 10);
        if (!Number.isNaN(index)) {
          this.goTo(index);
        }
      });
    });

    const dotsGroup = this.el.querySelector('.carousel__dots');
    if (dotsGroup) {
      dotsGroup.addEventListener('keydown', (e) => {
        if (this.total <= 1) return;

        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          this.prev();
          const activeDot = this.dots[this.currentIndex];
          if (activeDot) activeDot.focus();
        } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          this.next();
          const activeDot = this.dots[this.currentIndex];
          if (activeDot) activeDot.focus();
        } else if (e.key === 'Home') {
          e.preventDefault();
          this.goTo(0);
          if (this.dots[0]) this.dots[0].focus();
        } else if (e.key === 'End') {
          e.preventDefault();
          this.goTo(this.total - 1);
          if (this.dots[this.total - 1]) this.dots[this.total - 1].focus();
        }
      });
    }

    this.el.addEventListener('keydown', (e) => {
      if (this.total <= 1) return;

      // Arrow keys inside the 3D model slide rotate the three.js camera
      // (product-model-slide.js), not the carousel.
      if (e.target.closest && e.target.closest('[data-model-slide]')) return;

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.prev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.next();
      }
    });
  }

  _bindTouchSwipe() {
    const viewport = this.el.querySelector('.carousel__viewport');
    if (!viewport || this.total <= 1) return;

    const swipeThreshold = 50;

    viewport.addEventListener(
      'touchstart',
      (e) => {
        if (e.touches.length !== 1) return;
        // Drags on the 3D model slide rotate the model, never swipe the carousel.
        if (e.target.closest && e.target.closest('[data-model-slide]')) return;
        this._touchStartX = e.touches[0].clientX;
        this._touchStartY = e.touches[0].clientY;
        this._touchTracking = true;
      },
      { passive: true }
    );

    viewport.addEventListener(
      'touchend',
      (e) => {
        if (!this._touchTracking) return;
        this._touchTracking = false;

        const touch = e.changedTouches[0];
        const deltaX = touch.clientX - this._touchStartX;
        const deltaY = touch.clientY - this._touchStartY;

        if (Math.abs(deltaX) < swipeThreshold) return;
        if (Math.abs(deltaY) > Math.abs(deltaX)) return;

        if (deltaX < 0) {
          this.next();
        } else {
          this.prev();
        }
      },
      { passive: true }
    );

    viewport.addEventListener(
      'touchcancel',
      () => {
        this._touchTracking = false;
      },
      { passive: true }
    );
  }

  _bindLightbox() {
    if (!this.lightbox) return;

    if (this.zoomBtn) {
      this.zoomBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this._openLightbox();
      });
    }

    if (this.lightboxClose) {
      this.lightboxClose.addEventListener('click', () => this._closeLightbox());
    }
  }

  _bindVariantImageChange() {
    document.addEventListener('hattie:variant-image-change', (e) => {
      const imageId = e.detail && e.detail.imageId;
      if (!imageId) return;

      const index = this.images.findIndex((img) => img.id === imageId);
      if (index !== -1) {
        this.goTo(index);
      }
    });
  }

  _parseImageData() {
    const scriptEl = document.querySelector(
      '[data-carousel-images="' + this.sectionId + '"]'
    );
    if (!scriptEl) return [];
    try {
      return JSON.parse(scriptEl.textContent);
    } catch (_) {
      return [];
    }
  }

  _setTrackOffset(index) {
    if (!this.track) return;
    this.track.style.transform = 'translateX(-' + index * 100 + '%)';
  }

  _syncDots(activeIndex) {
    this.dots.forEach((dot, i) => {
      const isActive = i === activeIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  _syncSlideControls(activeIndex) {
    const slide = this.slides[activeIndex];
    const isModelSlide = slide ? slide.hasAttribute('data-model-slide') : false;

    // The 3D model slide is a standalone drag-to-rotate experience: the
    // prev/next click zones overlay the whole viewport, so they are hidden
    // while it is active — clicking never advances the carousel and pointer
    // events reach the three.js canvas for OrbitControls. Dots (and arrow
    // keys while the carousel container is focused) still navigate away.
    [this.prevBtn, this.nextBtn].forEach((zone) => {
      if (!zone) return;
      if (isModelSlide && document.activeElement === zone) {
        // Don't strand keyboard focus on a button about to be hidden.
        this.el.focus();
      }
      zone.hidden = isModelSlide;
    });

    if (this.zoomBtn) {
      if (isModelSlide && document.activeElement === this.zoomBtn) {
        // Don't strand keyboard focus on a button about to be hidden. The
        // carousel container has tabindex="0" whenever a model slide exists.
        this.el.focus();
      }
      this.zoomBtn.hidden = isModelSlide;
    }

    if (this.dragHint) {
      this.dragHint.hidden = !isModelSlide;
    }
  }

  /**
   * Advance to a specific slide by index (0-based). Wraps around.
   * @param {number} index
   */
  goTo(index) {
    if (this.total <= 1) return;

    const next = ((index % this.total) + this.total) % this.total;
    if (next === this.currentIndex) return;

    const prev = this.currentIndex;

    this._setTrackOffset(next);

    this.slides[prev].classList.remove('is-active');
    this.slides[prev].setAttribute('aria-hidden', 'true');
    this.slides[prev].toggleAttribute('inert', true);

    this.slides[next].classList.add('is-active');
    this.slides[next].setAttribute('aria-hidden', 'false');
    this.slides[next].toggleAttribute('inert', false);

    this._syncDots(next);
    this._syncSlideControls(next);

    this.currentIndex = next;

    if (typeof announceToScreenReader === 'function') {
      const isModelSlide = this.slides[next].hasAttribute('data-model-slide');
      announceToScreenReader(
        isModelSlide
          ? '3D model, slide ' + (next + 1) + ' of ' + this.total
          : 'Image ' + (next + 1) + ' of ' + this.total
      );
    }

    this.el.dispatchEvent(
      new CustomEvent('hattie:carousel-slide-change', {
        bubbles: true,
        detail: { index: next, previousIndex: prev, slide: this.slides[next] },
      })
    );
  }

  next() {
    this.goTo(this.currentIndex + 1);
  }

  prev() {
    this.goTo(this.currentIndex - 1);
  }

  _getActiveSlideIndex() {
    const activeSlide = this.slides.find((slide) => slide.classList.contains('is-active'));
    if (activeSlide) {
      const index = Number.parseInt(activeSlide.getAttribute('data-slide-index'), 10);
      if (!Number.isNaN(index)) return index;
    }
    return this.currentIndex;
  }

  _updateLightboxImage(index) {
    if (!this.lightboxImg) return;

    const imageData = this.images[index];
    if (imageData) {
      this.lightboxImg.src = imageData.src;
      this.lightboxImg.alt = imageData.alt;
    } else {
      const slideImg = this.slides[index]?.querySelector('.carousel__image');
      if (slideImg) {
        this.lightboxImg.src = slideImg.currentSrc || slideImg.src;
        this.lightboxImg.alt = slideImg.alt;
      }
    }

    // Shopify image_tag outputs srcset; the browser keeps showing the first image
    // unless srcset is cleared when swapping the lightbox source.
    this.lightboxImg.removeAttribute('srcset');
    this.lightboxImg.removeAttribute('sizes');
  }

  _openLightbox() {
    if (!this.lightbox) return;

    const index = this._getActiveSlideIndex();
    this._updateLightboxImage(index);

    this._lightboxTrigger = this.zoomBtn;

    this.lightbox.hidden = false;
    this.lightbox.scrollTop = 0;
    if (typeof lockPageScroll === 'function') lockPageScroll();
    focusTrap(this.lightbox);
    document.dispatchEvent(new CustomEvent('hattie:lightbox-opened'));

    if (this.lightboxClose) {
      this.lightboxClose.focus();
    }

    this._lightboxEscHandler = (e) => {
      if (e.key === 'Escape') this._closeLightbox();
    };
    document.addEventListener('keydown', this._lightboxEscHandler);
  }

  _closeLightbox() {
    if (!this.lightbox) return;

    this.lightbox.hidden = true;
    if (typeof unlockPageScroll === 'function') unlockPageScroll();
    releaseFocusTrap();
    document.dispatchEvent(new CustomEvent('hattie:lightbox-closed'));

    if (this._lightboxEscHandler) {
      document.removeEventListener('keydown', this._lightboxEscHandler);
      this._lightboxEscHandler = null;
    }

    if (this._lightboxTrigger) {
      this._lightboxTrigger.focus();
      this._lightboxTrigger = null;
    }
  }
}

function initProductCarousels() {
  document.querySelectorAll('[data-carousel]').forEach((el) => {
    if (!el.dataset.carouselInitialized) {
      el.dataset.carouselInitialized = 'true';
      new ProductCarousel(el);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductCarousels);
} else {
  initProductCarousels();
}

if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', initProductCarousels);
}
